// Carrega e atualiza o doc `users/{uid}` no Firestore — moeda principal,
// cotações personalizadas, dados de conta (nome/telefone/documento) e
// preferência de tema. Substitui chamadas antigas que liam diretamente do
// Firestore Auth (displayName) — agora lemos do `users/{uid}.name` quando
// preenchido, com fallback pro displayName.
//
// Schema esperado:
//   users/{uid} = {
//     email: string,
//     displayName: string?,           // do Auth (read-only do nosso lado)
//     name: string?,                  // sobrescreve displayName em UI
//     phone: string?,
//     document: string?,
//     primaryCurrency: string?,       // 'BRL' | 'USD' | 'EUR' | 'GBP' | 'CHF' | 'ARS'
//     currencyRates: object?,         // { USD: 5.50, EUR: 6.10, ... }
//     themePreference: string?,       // 'light' | 'dark' | 'system'
//     ...timestamps
//   }
//
// Nota: as Firestore Rules já permitem read/write de `users/{uid}` quando
// request.auth.uid == uid. Veja firestore.rules.

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './firebase.js';

// A lista de moedas suportadas vive em `core/currencies.js` (dados puros, sem
// Firebase) pra que o backend também possa importá-la. Re-exportamos aqui pra
// manter compat com todos os imports antigos (`from userSettings.js`).
export { SUPPORTED_CURRENCIES, findCurrency, DEFAULT_VET_MARKUP_PCT, roundRate } from './currencies.js';

let _cachePromise = null;

/** Limpa o cache em memória (útil após updates). */
export function invalidateUserSettings() {
  _cachePromise = null;
}

/**
 * Carrega settings do user atual. Cache in-memory por sessão.
 * Retorna null se não houver user logado.
 */
export function getUserSettings({ force = false } = {}) {
  if (force) _cachePromise = null;
  if (_cachePromise) return _cachePromise;
  _cachePromise = (async () => {
    const u = auth.currentUser;
    if (!u) return null;
    try {
      const snap = await getDoc(doc(db, 'users', u.uid));
      const data = snap.exists() ? snap.data() : {};
      return { uid: u.uid, ...data };
    } catch (err) {
      console.warn('[userSettings] read failed:', err.message);
      return { uid: u.uid };
    }
  })();
  return _cachePromise;
}

/**
 * Atualiza um subconjunto de campos de `users/{uid}` (merge).
 * Aceita qualquer subset do schema. Falha se não houver user logado.
 */
export async function updateUserSettings(patch) {
  const u = auth.currentUser;
  if (!u) throw new Error('Não autenticado');
  await setDoc(
    doc(db, 'users', u.uid),
    { ...patch, updatedAt: serverTimestamp() },
    { merge: true },
  );
  invalidateUserSettings();
}

/**
 * Resolve o "nome de exibição" do user com fallback:
 *   users/{uid}.name → auth.displayName → 'Viajante'
 */
export function resolveDisplayName(settings, user) {
  return (settings?.name || user?.displayName || 'Viajante').trim() || 'Viajante';
}

/** Pega a primeira palavra (primeiro nome) pra saudações. */
export function firstName(displayName) {
  return String(displayName || 'Viajante').trim().split(/\s+/)[0];
}
